import { useEffect, useState } from "react";
import { Bell, BellOff, BellRing, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const standalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

function toUint8(base64) {
  const pad = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

async function currentSubscription() {
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

export function NotifyToggle() {
  const [state, setState] = useState("loading");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported()) return setState(isIOS() && !standalone() ? "ios-install" : "unsupported");
    if (Notification.permission === "denied") return setState("denied");
    currentSubscription()
      .then((s) => setState(s ? "on" : "off"))
      .catch(() => setState("off"));
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setState(perm === "denied" ? "denied" : "off");
        return;
      }
      const { data } = await api.get("/push/vapid-public-key");
      const reg = await navigator.serviceWorker.ready;
      const sub = (await reg.pushManager.getSubscription()) || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toUint8(data.key) }));
      await api.post("/push/subscribe", sub.toJSON());
      await api.post("/push/test").catch(() => {});
      setState("on");
      toast.success("Notifiche attive: ti avviseremo ogni mattina");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const sub = await currentSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setState("off");
      toast.success("Notifiche disattivate");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const base = "flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-wider border transition-colors disabled:opacity-50";
  if (state === "loading") return null;
  if (state === "on")
    return (
      <button onClick={disable} disabled={busy} data-testid="notify-toggle-off" className={`${base} border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/10`}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <BellRing size={14} />} Notifiche attive
      </button>
    );
  if (state === "off")
    return (
      <button onClick={enable} disabled={busy} data-testid="notify-toggle-on" className={`${base} border-[#FFCC00] text-[#FFCC00] hover:bg-[#FFCC00] hover:text-black`}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Bell size={14} />} Avvisami ogni mattina
      </button>
    );
  const msg = {
    "ios-install": "Su iPhone installa prima l'app nella schermata Home (Condividi → Aggiungi a Home), poi aprila da lì per attivare le notifiche.",
    denied: "Notifiche bloccate: abilitale dalle impostazioni del browser per questo sito.",
    unsupported: "Questo browser non supporta le notifiche push.",
  }[state];
  return (
    <div className="flex items-start gap-2 text-[11px] text-[#A1A1AA] max-w-sm" data-testid={`notify-${state}`}>
      <BellOff size={14} className="shrink-0 mt-0.5" /> {msg}
    </div>
  );
}
